import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import dayjs from 'dayjs';
import { QmRecord, QmCrudStatus } from '../../api/qmCrud';
import {
  QM_CREATION_ATTACHMENT_COMMENT_PREFIX_MULTI,
  QM_CREATION_ATTACHMENT_COMMENT_PREFIX_SINGLE
} from '../../common/constants';

type HistoryItem = {
  id: string;
  type: 'STATUS' | 'CHANGE' | 'ACTION';
  time: string;
  title: string;
  detail: string;
  by: string;
};

type HistoryGroup = {
  id: string;
  time: string;
  items: HistoryItem[];
};

type ReasonOption = {
  code: string;
  label: string;
};

type QmHistoryTimelineProps = {
  selectedQm: QmRecord;
  reasonOptions: ReasonOption[];
  sectionCardSx: Record<string, unknown>;
  formatStatusLabel: (status: QmCrudStatus | string | null | undefined) => string;
};

const CHANGE_FIELD_LABELS: Record<string, string> = {
  reasonCode: 'Reason',
  poNumber: 'PO Number',
  poLine: 'PO Line',
  poLineId: 'PO Line ID',
  itemId: 'Item ID',
  sscc: 'SSCC',
  asn: 'ASN',
  supplierName: 'Supplier',
  contactName: 'Contact',
  itemLocation: 'Item Location',
  packageCount: 'Package Count',
  packageType: 'Package Type',
  description: 'Description',
  closureCode: 'Closure Code',
  closureNotes: 'Closure Notes',
  otherInformation: 'Other Information'
};

function classifyChangeAction(oldValue: string, newValue: string): 'CREATE' | 'UPDATE' | 'CLEAR' {
  const normalize = (value: string) =>
    value
      .trim()
      .replace(/^['"]+|['"]+$/g, '')
      .toLowerCase();

  const oldNormalized = normalize(oldValue);
  const newNormalized = normalize(newValue);

  const oldIsEmpty = oldNormalized === 'null' || oldNormalized === 'undefined' || oldNormalized === '';
  const newIsEmpty = newNormalized === 'null' || newNormalized === 'undefined' || newNormalized === '';

  if (oldIsEmpty && !newIsEmpty) return 'CREATE';
  if (!oldIsEmpty && newIsEmpty) return 'CLEAR';
  return 'UPDATE';
}

function classifyChangeType(
  fieldName: string,
  oldValue: string,
  newValue: string
): 'CREATE' | 'UPDATE' | 'CLEAR' | 'CLOSURE' {
  const baseType = classifyChangeAction(oldValue, newValue);
  const normalizedField = fieldName.trim().toLowerCase();
  const isClosureField = normalizedField === 'closurecode' || normalizedField === 'closurenotes';

  if (baseType === 'CREATE' && isClosureField) return 'CLOSURE';
  return baseType;
}

function parseChangeDetail(detail: string): { oldRaw: string; newRaw: string } {
  const parts = detail.split(/\s*(?:->|→|=>)\s*/);
  if (parts.length >= 2) {
    return {
      oldRaw: parts[0] || '',
      newRaw: parts.slice(1).join(' -> ') || ''
    };
  }

  return {
    oldRaw: '',
    newRaw: detail
  };
}

type HistoryTone = {
  accent: string;
  chipBg: string;
  chipText: string;
  chipBorder: string;
};

function getHistoryTone(label: string): HistoryTone {
  switch (label) {
    case 'STATUS':
      return {
        accent: '#3b82f6',
        chipBg: '#f1f7ff',
        chipText: '#1e40af',
        chipBorder: '#c7dbff'
      };
    case 'CREATE':
    case 'CREATION':
      return {
        accent: '#0d9488',
        chipBg: '#f2fffd',
        chipText: '#0f766e',
        chipBorder: '#b8efe8'
      };
    case 'CLOSURE':
      return {
        accent: '#d97706',
        chipBg: '#fffbf2',
        chipText: '#92400e',
        chipBorder: '#f7deb2'
      };
    case 'ACTION':
      return {
        accent: '#be185d',
        chipBg: '#fff1f7',
        chipText: '#9d174d',
        chipBorder: '#f9c6dd'
      };
    case 'CLEAR':
      return {
        accent: '#ea580c',
        chipBg: '#fff7f1',
        chipText: '#9a3412',
        chipBorder: '#f9d3bf'
      };
    case 'UPDATE':
    default:
      return {
        accent: '#64748b',
        chipBg: '#f8fafc',
        chipText: '#475569',
        chipBorder: '#dbe3ec'
      };
  }
}

function toActorDisplay(value: string | null | undefined): string {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();
  return normalized || '-';
}

function parseChangeFieldName(item: HistoryItem): string {
  if (item.type !== 'CHANGE') return '';
  return item.title.replace(/^Field changed:\s*/i, '').trim();
}

function isCreationAttachmentAction(detail: string): boolean {
  const normalized = String(detail || '')
    .trim()
    .toLowerCase();
  return (
    normalized.startsWith(QM_CREATION_ATTACHMENT_COMMENT_PREFIX_SINGLE.toLowerCase()) ||
    normalized.startsWith(QM_CREATION_ATTACHMENT_COMMENT_PREFIX_MULTI.toLowerCase())
  );
}

function formatHistoryDetail(item: HistoryItem) {
  if (item.type !== 'CHANGE') return item.detail;

  const { oldRaw, newRaw } = parseChangeDetail(item.detail);
  const fieldName = parseChangeFieldName(item);
  const actionType = classifyChangeType(fieldName, oldRaw, newRaw);

  if (actionType === 'CREATE') return `Created with ${newRaw}`;
  if (actionType === 'CLOSURE') return `Closure set to ${newRaw}`;
  if (actionType === 'CLEAR') return `Cleared from ${oldRaw}`;
  return `${oldRaw} -> ${newRaw}`;
}

function getHistoryChip(item: HistoryItem): { label: string } {
  if (item.type === 'STATUS') return { label: 'STATUS' };
  if (item.type === 'ACTION') {
    return { label: isCreationAttachmentAction(item.detail) ? 'CREATION' : 'ACTION' };
  }

  const { oldRaw, newRaw } = parseChangeDetail(item.detail);
  const fieldName = parseChangeFieldName(item);
  const actionType = classifyChangeType(fieldName, oldRaw, newRaw);
  if (actionType === 'CREATE') return { label: 'CREATE' };
  if (actionType === 'CLOSURE') return { label: 'CLOSURE' };
  if (actionType === 'CLEAR') return { label: 'CLEAR' };
  return { label: 'UPDATE' };
}

function isCreateChangeItem(item: HistoryItem): boolean {
  if (item.type !== 'CHANGE') return false;
  const fieldName = parseChangeFieldName(item);
  const { oldRaw, newRaw } = parseChangeDetail(item.detail);
  return classifyChangeType(fieldName, oldRaw, newRaw) === 'CREATE';
}

function isClosureChangeItem(item: HistoryItem): boolean {
  if (item.type !== 'CHANGE') return false;
  const fieldName = parseChangeFieldName(item);
  const { oldRaw, newRaw } = parseChangeDetail(item.detail);
  return classifyChangeType(fieldName, oldRaw, newRaw) === 'CLOSURE';
}

function toDisplayValue(
  fieldName: string,
  rawValue: string,
  reasonOptions: ReasonOption[],
  selectedQm: QmRecord,
  formatStatusLabel: (status: QmCrudStatus | string | null | undefined) => string
): string {
  const normalized = rawValue.trim();
  if (!normalized || normalized.toLowerCase() === 'null') return '-';

  if (fieldName === 'status') {
    return formatStatusLabel(normalized);
  }

  if (fieldName === 'reasonCode') {
    const reason = reasonOptions.find((item) => item.code === normalized);
    if (reason?.label) return reason.label;
    if (selectedQm.reasonCode === normalized && selectedQm.reasonLabel) return selectedQm.reasonLabel;
    return normalized;
  }

  return normalized;
}

function renderHistoryItemCard(item: HistoryItem) {
  const chip = getHistoryChip(item);
  const tone = getHistoryTone(chip.label);

  return (
    <Paper
      key={item.id}
      variant="outlined"
      sx={{
        p: 0.85,
        borderRadius: 1.25,
        borderLeft: '3px solid',
        borderLeftColor: tone.accent,
        bgcolor: 'background.default'
      }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.2 }}>
        <Stack direction="row" alignItems="center" spacing={0.6}>
          <Chip
            size="small"
            label={chip.label}
            variant="outlined"
            sx={{
              bgcolor: tone.chipBg,
              color: tone.chipText,
              borderColor: tone.chipBorder,
              fontWeight: 700
            }}
          />
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{item.title}</Typography>
        </Stack>
        <Stack spacing={0.1} alignItems="flex-end">
          <Typography sx={{ fontSize: '0.64rem', color: 'text.secondary' }}>
            {dayjs(item.time).format('DD/MM/YYYY HH:mm:ss')}
          </Typography>
          <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary' }}>{toActorDisplay(item.by)}</Typography>
        </Stack>
      </Stack>
      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.3 }}>
        {formatHistoryDetail(item)}
      </Typography>
    </Paper>
  );
}

export default function QmHistoryTimeline({
  selectedQm,
  reasonOptions,
  sectionCardSx,
  formatStatusLabel
}: QmHistoryTimelineProps) {
  const historyItems: HistoryItem[] = useMemo(() => {
    const statusItems: HistoryItem[] = selectedQm.statusHistory.map((entry) => ({
      id: `status-${entry.id}`,
      type: 'STATUS',
      time: entry.changedAt,
      title: `Status ${formatStatusLabel(entry.fromStatus)} -> ${formatStatusLabel(entry.toStatus)}`,
      detail: entry.note || 'No note',
      by: entry.changedBy
    }));

    const changeItems: HistoryItem[] = selectedQm.changeHistory
      .filter(
        (entry) =>
          String(entry.fieldName || '')
            .trim()
            .toLowerCase() !== 'status'
      )
      .map((entry) => ({
        id: `change-${entry.id}`,
        type: 'CHANGE',
        time: entry.changedAt,
        title: `Field changed: ${entry.fieldName}`,
        detail: `${entry.oldValue ?? 'null'} -> ${entry.newValue ?? 'null'}`,
        by: entry.changedBy
      }));

    const actionItems: HistoryItem[] = (selectedQm.comments || []).map((entry) => ({
      id: `action-${entry.id}`,
      type: 'ACTION',
      time: entry.createdAt,
      title: isCreationAttachmentAction(entry.body) ? 'Creation' : 'Action',
      detail: entry.body,
      by: entry.createdBy
    }));

    return [...statusItems, ...changeItems, ...actionItems].sort(
      (a, b) => dayjs(b.time).valueOf() - dayjs(a.time).valueOf()
    );
  }, [formatStatusLabel, selectedQm]);

  const historyGroups: HistoryGroup[] = useMemo(() => {
    const groups: HistoryGroup[] = [];
    const groupIndexByTime = new Map<string, number>();

    for (const item of historyItems) {
      const timeKey = dayjs(item.time).format('YYYY-MM-DD HH:mm:ss');
      const existingIndex = groupIndexByTime.get(timeKey);

      if (existingIndex !== undefined) {
        groups[existingIndex].items.push(item);
        continue;
      }

      groupIndexByTime.set(timeKey, groups.length);
      groups.push({
        id: `group-${timeKey}`,
        time: item.time,
        items: [item]
      });
    }

    return groups;
  }, [historyItems]);

  const historyCardCount = useMemo(() => {
    return historyGroups.reduce((count, group) => {
      const parsedCreateChanges = group.items.map((item) => {
        if (item.type !== 'CHANGE') return null;

        const { oldRaw, newRaw } = parseChangeDetail(item.detail);
        const fieldName = parseChangeFieldName(item);
        const actionType = classifyChangeType(fieldName, oldRaw, newRaw);

        return {
          id: item.id,
          fieldName,
          actionType
        };
      });

      const creationEntries = parsedCreateChanges
        .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
        .filter((entry) => entry.actionType === 'CREATE');
      const closureEntries = parsedCreateChanges
        .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
        .filter((entry) => entry.actionType === 'CLOSURE');
      const creationIds = new Set(creationEntries.map((entry) => entry.id));
      const closureIds = new Set(closureEntries.map((entry) => entry.id));
      const actionEntries = group.items.filter((item) => item.type === 'ACTION');
      const creationActionIds = new Set(
        actionEntries.filter((item) => isCreationAttachmentAction(item.detail)).map((item) => item.id)
      );
      const statusEntries = group.items.filter((item) => item.type === 'STATUS');
      const hasNonCreationActionItems = actionEntries.some((item) => !isCreationAttachmentAction(item.detail));
      const hasCreationActionItems = creationActionIds.size > 0;
      const hasStatusItems = statusEntries.length > 0;
      const summaryCardType =
        !hasNonCreationActionItems && (creationEntries.length > 0 || hasCreationActionItems)
          ? 'CREATION'
          : !hasNonCreationActionItems && closureEntries.length > 0
            ? 'CLOSURE'
            : hasNonCreationActionItems
              ? 'ACTION'
              : hasStatusItems
                ? 'STATUS'
                : null;

      const itemsToRender =
        summaryCardType === 'CREATION'
          ? group.items.filter((item) => !creationIds.has(item.id) && !creationActionIds.has(item.id))
          : summaryCardType === 'CLOSURE'
            ? group.items.filter((item) => !closureIds.has(item.id))
            : summaryCardType === 'ACTION'
              ? group.items.filter((item) => item.type !== 'ACTION' || creationActionIds.has(item.id))
              : summaryCardType === 'STATUS'
                ? group.items.filter((item) => item.type !== 'STATUS')
                : group.items;

      if (summaryCardType) {
        return count + 1 + itemsToRender.length;
      }

      return count + itemsToRender.length;
    }, 0);
  }, [historyGroups]);

  return (
    <Paper variant="outlined" sx={sectionCardSx}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">History</Typography>
        <Chip size="small" label={`${historyCardCount} entries`} variant="outlined" />
      </Stack>
      <Stack spacing={1} sx={{ maxHeight: 420, overflowY: 'auto' }}>
        {historyGroups.map((group) => {
          const uniqueActors = Array.from(
            new Set(group.items.map((item) => toActorDisplay(item.by)).filter((actor) => actor !== '-'))
          );
          const singleActor = uniqueActors.length === 1 ? uniqueActors[0] : null;

          const parsedCreateChanges = group.items.map((item) => {
            if (item.type !== 'CHANGE') return null;

            const { oldRaw, newRaw } = parseChangeDetail(item.detail);
            const fieldName = parseChangeFieldName(item);
            const actionType = classifyChangeType(fieldName, oldRaw, newRaw);

            return {
              id: item.id,
              fieldName,
              oldRaw,
              newRaw,
              actionType
            };
          });

          const creationEntries = parsedCreateChanges
            .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
            .filter((entry) => entry.actionType === 'CREATE');
          const closureEntries = parsedCreateChanges
            .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
            .filter((entry) => entry.actionType === 'CLOSURE');
          const actionEntries = group.items
            .filter((item) => item.type === 'ACTION')
            .map((item) => ({
              id: item.id,
              label: 'Action Comment',
              value: item.detail,
              isCreationAttachment: isCreationAttachmentAction(item.detail)
            }));
          const creationActionEntries = actionEntries.filter((entry) => entry.isCreationAttachment);
          const nonCreationActionEntries = actionEntries.filter((entry) => !entry.isCreationAttachment);
          const statusEntries = group.items
            .filter((item) => item.type === 'STATUS')
            .map((item) => ({
              id: item.id,
              label: 'Status Change',
              value:
                item.detail && item.detail !== 'No note'
                  ? `${item.title.replace(/^Status\s*/i, '')} | ${item.detail}`
                  : item.title.replace(/^Status\s*/i, '')
            }));
          const hasNonCreationActionItems = nonCreationActionEntries.length > 0;
          const hasCreationActionItems = creationActionEntries.length > 0;
          const hasStatusItems = statusEntries.length > 0;
          const summaryCardType =
            !hasNonCreationActionItems && (creationEntries.length > 0 || hasCreationActionItems)
              ? 'CREATION'
              : !hasNonCreationActionItems && closureEntries.length > 0
                ? 'CLOSURE'
                : hasNonCreationActionItems
                  ? 'ACTION'
                  : hasStatusItems
                    ? 'STATUS'
                    : null;
          const summaryEntries = summaryCardType === 'CREATION' ? creationEntries : closureEntries;
          const itemsToRender =
            summaryCardType === 'CREATION'
              ? group.items.filter(
                  (item) =>
                    !isCreateChangeItem(item) && !(item.type === 'ACTION' && isCreationAttachmentAction(item.detail))
                )
              : summaryCardType === 'CLOSURE'
                ? group.items.filter((item) => !isClosureChangeItem(item))
                : summaryCardType === 'ACTION'
                  ? group.items.filter(
                      (item) =>
                        item.type !== 'ACTION' || (item.type === 'ACTION' && isCreationAttachmentAction(item.detail))
                    )
                  : summaryCardType === 'STATUS'
                    ? group.items.filter((item) => item.type !== 'STATUS')
                    : group.items;

          if (summaryCardType) {
            const orderedSummaryEntries = summaryEntries.sort((a, b) => {
              const priority = (fieldName: string) => {
                if (fieldName === 'closureCode') return 1;
                if (fieldName === 'closureNotes') return 2;
                if (fieldName === 'reasonCode') return 1;
                if (fieldName === 'poNumber') return 2;
                if (fieldName === 'poLine') return 3;
                if (fieldName === 'poLineId') return 4;
                if (fieldName === 'itemId') return 5;
                if (fieldName === 'itemLocation') return 6;
                return 50;
              };
              return priority(a.fieldName) - priority(b.fieldName);
            });
            const summaryDisplayEntries =
              summaryCardType === 'ACTION'
                ? nonCreationActionEntries
                : summaryCardType === 'STATUS'
                  ? statusEntries
                  : summaryCardType === 'CREATION'
                    ? [
                        ...orderedSummaryEntries.map((entry) => ({
                          id: entry.id,
                          label: CHANGE_FIELD_LABELS[entry.fieldName] || entry.fieldName || 'Field',
                          value: toDisplayValue(
                            entry.fieldName,
                            entry.newRaw,
                            reasonOptions,
                            selectedQm,
                            formatStatusLabel
                          )
                        })),
                        ...creationActionEntries.map((entry) => ({
                          id: entry.id,
                          label: 'Attachment',
                          value: entry.value
                        }))
                      ]
                    : orderedSummaryEntries.map((entry) => ({
                        id: entry.id,
                        label: CHANGE_FIELD_LABELS[entry.fieldName] || entry.fieldName || 'Field',
                        value: toDisplayValue(
                          entry.fieldName,
                          entry.newRaw,
                          reasonOptions,
                          selectedQm,
                          formatStatusLabel
                        )
                      }));
            const summaryTone = getHistoryTone(summaryCardType);

            return (
              <Stack key={group.id} spacing={0.8}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.1,
                    borderRadius: 1.5,
                    borderLeft: '3px solid',
                    borderLeftColor: summaryTone.accent,
                    bgcolor: 'background.default'
                  }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                    <Chip
                      size="small"
                      label={summaryCardType}
                      variant="outlined"
                      sx={{
                        bgcolor: summaryTone.chipBg,
                        color: summaryTone.chipText,
                        borderColor: summaryTone.chipBorder,
                        fontWeight: 700
                      }}
                    />
                    <Stack spacing={0.1} alignItems="flex-end">
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.66rem' }}>
                        {dayjs(group.time).format('DD/MM/YYYY HH:mm:ss')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.66rem' }}>
                        {singleActor || 'multiple users'}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 0.6
                    }}>
                    {summaryDisplayEntries.map((entry) => {
                      return (
                        <Box
                          key={entry.id}
                          sx={{
                            px: 0.8,
                            py: 0.55,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1
                          }}>
                          <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', fontWeight: 600 }}>
                            {entry.label}
                          </Typography>
                          <Typography sx={{ fontSize: '0.72rem', color: 'text.primary', lineHeight: 1.3 }}>
                            {entry.value || '-'}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>
                {itemsToRender.length > 0 ? (
                  <Stack spacing={0.65}>{itemsToRender.map((item) => renderHistoryItemCard(item))}</Stack>
                ) : null}
              </Stack>
            );
          }

          return (
            <Stack key={group.id} spacing={0.65}>
              {itemsToRender.map((item) => renderHistoryItemCard(item))}
            </Stack>
          );
        })}
      </Stack>
    </Paper>
  );
}
